import React from "react";
import { useStore } from "zustand";
import type { EditorState, EditorStore } from "./createEditorStore";

const EditorStoreContext = React.createContext<EditorStore | null>(null);

export interface EditorStoreProviderProps {
  store: EditorStore;
  children: React.ReactNode;
}

export const EditorStoreProvider: React.FC<EditorStoreProviderProps> = ({
  store,
  children
}) => (
  <EditorStoreContext.Provider value={store}>
    {children}
  </EditorStoreContext.Provider>
);

/** The store when rendered inside an editor, null in a bare viewer. */
export const useOptionalEditorStore = (): EditorStore | null =>
  React.useContext(EditorStoreContext);

export const useEditorStore = (): EditorStore => {
  const store = React.useContext(EditorStoreContext);
  if (!store) {
    throw new Error("useEditorStore must be used within EditorStoreProvider");
  }
  return store;
};

/** Subscribes to one slice. Components must select the smallest slice
 *  they need: a shape subscribes to `shapes[id]`, never to `shapes`. */
export const useEditorSelector = <T,>(selector: (state: EditorState) => T): T =>
  useStore(useEditorStore(), selector);
